import type { GamesWithWinsParams, GameWithWinsDto } from '../games/games.schema';
import type { PaginationType } from '../types/Pagination.types';
import type { GameLastWinResponse, GameProviderRtpResponse, GameUserRtpResponse, ReportPayloadDto, RoundsWithWinsParams, RoundWithWinDto, SharedRoundDto, ShareTokenDto } from './fair-play.schema';
import { validateWithZod } from '@/utils/zod.utils';
import { ENDPOINTS } from '../endpoints';
import { GamesWithWinsParamsSchema, GameWithWinsDtoSchema } from '../games/games.schema';

import { httpClient } from '../httpClient';
import { PaginationSchema } from '../types/Pagination.types';
import { GameLastWinDtoSchema, GameProviderRtpResponseDtoSchema, GameUserRtpResponseDtoSchema, ReportResponseDtoSchema, RoundsWithWinsParamsSchema, RoundWithWinDtoSchema, SharedRoundDtoSchema, ShareTokenDtoSchema } from './fair-play.schema';

export type GetGamesWithWinsParams = GamesWithWinsParams;

export const fairPlayApi = {

  async getGameProviderRtp(providerId: string, gameId: string) {
    const response = await httpClient.get<GameProviderRtpResponse>(
      ENDPOINTS.fairPlay.gameProviderRtp(providerId, gameId),
    );

    return validateWithZod(GameProviderRtpResponseDtoSchema, response, 'backend');
  },

  async getGameLastWin(userId: string, gameId: string) {
    const response = await httpClient.get<GameLastWinResponse>(
      ENDPOINTS.fairPlay.gameLastWin(userId, gameId),
    );

    return validateWithZod(GameLastWinDtoSchema, response, 'backend');
  },

  async getGameUserRtp(gameId: string) {
    const response = await httpClient.get<GameUserRtpResponse>(
      ENDPOINTS.fairPlay.gameUserRtp(gameId),
    );
    return validateWithZod(GameUserRtpResponseDtoSchema, response, 'backend');
  },

  async getGamesWithWins(params?: GetGamesWithWinsParams) {
    const validatedParams = params ? validateWithZod(GamesWithWinsParamsSchema, params, 'frontend') : undefined;

    const response = await httpClient.get<PaginationType<GameWithWinsDto>>(
      ENDPOINTS.fairPlay.gamesWithWins,
      validatedParams ? { params: validatedParams } : undefined,
    );

    return validateWithZod(PaginationSchema(GameWithWinsDtoSchema), response, 'backend');
  },

  async getRoundsWithWins(gameId: string, params?: RoundsWithWinsParams) {
    const validatedParams = params ? validateWithZod(RoundsWithWinsParamsSchema, params, 'frontend') : undefined;

    const response = await httpClient.get<PaginationType<RoundWithWinDto>>(
      ENDPOINTS.games.roundsWithWins(gameId),
      validatedParams ? { params: validatedParams } : undefined,
    );

    return validateWithZod(PaginationSchema(RoundWithWinDtoSchema), response, 'backend');
  },

  async verifyRound(roundId: string) {
    return await httpClient.post<RoundWithWinDto>(
      ENDPOINTS.games.verifyRound(roundId),
    );
  },

  async generateReport(gameId: string, data: ReportPayloadDto) {
    const response = await httpClient.post(
      ENDPOINTS.fairPlay.report(gameId),
      data,
    );

    return validateWithZod(ReportResponseDtoSchema, response, 'backend');
  },

  async getSharedRound(token: string): Promise<SharedRoundDto> {
    const response = await httpClient.get<SharedRoundDto>(ENDPOINTS.games.sharedRound(token), { skipAuth: true });

    return validateWithZod(SharedRoundDtoSchema, response, 'backend');
  },

  async createShareLink(gameId: string, roundId: string): Promise<ShareTokenDto> {
    const response = await httpClient.post<ShareTokenDto>(ENDPOINTS.games.shareRound(gameId, roundId));

    return validateWithZod(ShareTokenDtoSchema, response, 'backend');
  },
};
